import { CommonModule, formatDate } from '@angular/common';
import { ChangeDetectionStrategy, Component, input, output } from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { ProgressBarModule } from 'primeng/progressbar';
import { TagModule } from 'primeng/tag';

import { ShipmentDelivery } from '../../models/delivery/shipment-delivery.model';
import { Shipment } from '../../models/shipment';
import { ShipmentDeliveryOperationStatus } from '../../models/workflow/shipment-workflow.model';
import { formatShipmentDispatchStatus, ShipmentDispatchPresenter } from './shipment-dispatch.presenter';

export type ShipmentDispatchCommand =
    | { type: 'assign-driver-vehicle' }
    | { type: 'invoke-all' }
    | { type: 'confirm-all' }
    | { type: 'invoke'; delivery: ShipmentDelivery }
    | { type: 'confirm'; delivery: ShipmentDelivery }
    | { type: 'cancel'; delivery: ShipmentDelivery }
    | { type: 'track'; delivery: ShipmentDelivery }
    | { type: 'amend'; delivery: ShipmentDelivery };

/** Display-only Shipment Dispatch workspace. The route shell owns all commands. */
@Component({
    selector: 'app-shipment-dispatch-panel',
    standalone: true,
    templateUrl: './shipment-dispatch-panel.component.html',
    styleUrls: ['./shipment-dispatch-panel.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, ButtonModule, ProgressBarModule, TagModule]
})
export class ShipmentDispatchPanelComponent {
    readonly shipment = input.required<Shipment>();
    readonly pendingJobs = input<ShipmentDelivery[]>([]);
    readonly deliveries = input<ShipmentDelivery[]>([]);
    readonly operationStatuses = input<Record<number, ShipmentDeliveryOperationStatus>>({});
    readonly invokeInProgress = input(false);
    readonly confirmInProgress = input(false);
    readonly canInvoke = input(false);
    readonly canConfirm = input(false);
    readonly canEdit = input(false);
    readonly canViewCommercials = input(false);
    readonly pickupLabel = input('Pickup pending');
    readonly dropoffLabel = input('Dropoff pending');
    readonly command = output<ShipmentDispatchCommand>();

    private readonly presenter = new ShipmentDispatchPresenter(value => value ? formatDate(new Date(value), 'dd-MM-yyyy HH:mm', 'en-US') : '');

    requestedCount(): number { return this.shipment().quantityRequired || 0; }
    tripCount(): number { return this.deliveries().length; }
    pendingCount(): number { return this.pendingJobs().length; }
    remainingAssignments(): number { return Math.max(this.requestedCount() - this.tripCount() - this.pendingCount(), 0); }
    fulfillmentPercent(): number { return this.requestedCount() ? Math.min(100, Math.round((this.tripCount() / this.requestedCount()) * 100)) : 0; }
    showBatchActions(): boolean { return this.pendingJobs().length > 0 && (this.canInvoke() || this.canConfirm()); }
    canInvokeJob(job: ShipmentDelivery): boolean {
        // A selected driver is precisely who an Invoke action is sent to.  Keep the
        // action available for both open and dispatcher-selected jobs.
        return this.canInvoke()
            && ['JOB_DETAILS_ENTRY', 'SEARCHING_VEHICLE', 'SELECTING_VEHICLE'].includes(String(job.jobStatus || '').toUpperCase());
    }
    canConfirmJob(job: ShipmentDelivery): boolean {
        return this.canConfirm() && !!job.deliveryDriver
            && ['SELECTING_VEHICLE', 'JOB_INVOKE_DRIVER', 'JOB_DRIVER_NEGOTIATING', 'JOB_ADVERTISE_INVOKE'].includes(String(job.jobStatus || '').toUpperCase());
    }
    deliveryProgress(delivery: ShipmentDelivery) { return this.presenter.deliveryProgress(delivery, this.operationStatuses()); }
    progressStepMeta = this.presenter.progressStepMeta.bind(this.presenter);
    progressBarStyleClass = this.presenter.progressBarStyleClass.bind(this.presenter);
    deliverySeverity = this.presenter.deliverySeverity.bind(this.presenter);
    driverName = this.presenter.driverName.bind(this.presenter);
    driverMeta = this.presenter.driverMeta.bind(this.presenter);
    vehicleLabel = this.presenter.vehicleLabel.bind(this.presenter);
    vehicleMeta = this.presenter.vehicleMeta.bind(this.presenter);
    assignmentSource = this.presenter.assignmentSource.bind(this.presenter);
    readiness = this.presenter.readiness.bind(this.presenter);
    milestones = this.presenter.milestones.bind(this.presenter);
    readonly formatStatus = formatShipmentDispatchStatus;

    updatedAt(delivery: ShipmentDelivery): string { return this.presenter.updatedAt(delivery, this.operationStatuses()); }
    linkedReleaseCount(delivery: ShipmentDelivery): number { return this.presenter.linkedReleaseCount(delivery, this.operationStatuses()); }
    commercialLine(delivery: ShipmentDelivery): string {
        const currency = this.shipment().countryOfOrder?.currencyCode || '';
        return this.presenter.commercialLine(delivery, this.canViewCommercials(), value => `${currency ? `${currency} ` : ''}${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
    }
    canAmend(delivery: ShipmentDelivery): boolean {
        const terminal = ['JOB_DRIVER_CANCELLED', 'JOB_DRIVER_REJECTED', 'DRIVER_JOB_COMPLETED', 'JOB_COMPLETION_ACKNOWLEDGE'];
        const workflow = String(delivery?.workflowState || '').toUpperCase();
        return !!delivery?.deliveryId && String(delivery.deliveryType || '').toUpperCase() === 'DELIVERY'
            && !terminal.includes(String(delivery.jobStatus || '').toUpperCase())
            && !['CANCELLED', 'DELIVERY_COMPLETE', 'CUSTOMER_ACKNOWLEDGED'].includes(workflow);
    }
}
